/**
 * NIP-17 direct messages, read as mail.
 *
 * A DM arrives the way mail does — a gift wrap addressed to the account — so it
 * is opened by the same unwrap and stored in the same index. What differs is
 * who vouches for the sender: postrd classifies email, and never sees a DM, so
 * a DM's box is decided here by the contact list alone.
 *
 * The sender's identity in that list is their verified NIP-05, which is shaped
 * like an address and sits beside email senders. A sender with none is known by
 * their npub.
 */
import { getEventHash, nip05, nip19, type Event as NostrEvent } from "nostr-tools";
import type { Mail } from "./seal";
import type { Profile } from "./profile";

/** NIP-17 chat message rumor. */
export const KIND_CHAT = 14;

export interface IdentityLookups {
  profileOf(pubkey: string): Promise<Profile | null>;
  /** Whether the domain names this pubkey under this NIP-05, as of now. */
  verify(pubkey: string, nip05: string): Promise<boolean>;
}

export interface Identity {
  /** Verified NIP-05 lowercased, or the npub. What the allowlist holds. */
  id: string;
  name: string;
}

export const defaultLookups = (profileOf: IdentityLookups["profileOf"]): IdentityLookups => ({
  profileOf,
  verify: (pubkey, name) => (nip05.isNip05(name) ? nip05.isValid(pubkey, name) : Promise.resolve(false)),
});

/**
 * Who a pubkey is, for the allowlist.
 *
 * A NIP-05 is only a claim until its domain confirms it: an unverified one would
 * let any key borrow a trusted contact's name and skip the Screener.
 */
export async function identify(pubkey: string, lookups: IdentityLookups): Promise<Identity> {
  const npub = nip19.npubEncode(pubkey);
  const profile = await lookups.profileOf(pubkey).catch(() => null);
  const name = profile?.name ?? "";
  const claimed = profile?.nip05?.trim().toLowerCase();
  if (claimed && claimed.includes("@") && (await lookups.verify(pubkey, claimed).catch(() => false))) {
    return { id: claimed.startsWith("_@") ? claimed.slice(2) : claimed, name };
  }
  return { id: npub, name };
}

/**
 * One chat rumor as a message.
 *
 * NIP-17 requires the rumor's author to be the seal's signer. Without that
 * check anyone could seal a rumor that claims to be from a trusted contact.
 */
export function dmOf(
  wrapId: string,
  seal: NostrEvent,
  rumor: NostrEvent,
  sender: Identity,
  ownPubkey: string,
): Mail {
  if (rumor.kind !== KIND_CHAT) throw new Error(`not a DM: rumor kind ${rumor.kind}`);
  if (rumor.pubkey !== seal.pubkey) throw new Error("DM rumor is not from the seal's signer");

  const tag = (name: string) => rumor.tags.find((t) => t[0] === name)?.[1];
  const from = sender.name ? `${sender.name} <${sender.id}>` : sender.id;
  const replyTo = tag("e");
  return {
    id: wrapId,
    sealedAt: rumor.created_at,
    message_id: getEventHash(rumor),
    date: new Date(rumor.created_at * 1000).toISOString(),
    from,
    to: rumor.tags.filter((t) => t[0] === "p" && t[1]).map((t) => nip19.npubEncode(t[1]!)),
    subject: tag("subject") ?? "",
    ...(replyTo ? { in_reply_to: replyTo } : {}),
    text: rumor.content,
    origin: "nostr",
    sender: sender.id,
    sender_pubkey: rumor.pubkey,
    sent: rumor.pubkey === ownPubkey,
  };
}
